import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_CUSTOMERS } from "../../../utils/constants";
import customerService from "../../../services/ticketService/customerService";
import { CustomerInterface } from "../../../models/TicketModule/Customer";

interface UpdateCustomersContext {
  previousCustomers: CustomerInterface[];
}

const useUpdateCustomers = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    CustomerInterface,
    Error,
    CustomerInterface,
    UpdateCustomersContext
  >({
    mutationFn: async (customer: CustomerInterface) => {
      // Assuming the ID is stored in the customer object or can be derived
      const { id, ...customerData } = customer;
      return customerService.Update(id || "", customerData);
    },

    onMutate: async (updatedCustomer: CustomerInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_CUSTOMERS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousCustomers =
        queryClient.getQueryData<CustomerInterface[]>([
          CACHE_KEY_CUSTOMERS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      queryClient.setQueryData<CustomerInterface[]>(
        [
          CACHE_KEY_CUSTOMERS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (customers = []) =>
          customers.map((customer) =>
            customer.id === updatedCustomer.id ? updatedCustomer : customer
          )
      );

      return { previousCustomers };
    },

    onSuccess: (savedCustomer) => {
      queryClient.setQueryData<CustomerInterface[]>(
        [
          CACHE_KEY_CUSTOMERS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (customers) =>
          customers?.map((customer) =>
            customer.id === savedCustomer.id ? savedCustomer : customer
          )
      );
    },

    onError: (error, updatedCustomer, context) => {
      if (!context) return;

      queryClient.setQueryData<CustomerInterface[]>(
        [
          CACHE_KEY_CUSTOMERS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousCustomers
      );
    },
  });
};

export default useUpdateCustomers;
